import { Link, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { requestNewChat } from "@/shared/lib/newChatSignal";
import { usePalette } from "@/shared/lib/hooks/useCommands";
import { formatShortcut } from "@/shared/lib/shortcut";
import { BrandMark, Button, Kbd } from "@/shared/ui";
import { NAV_ITEMS } from "../nav";
import { Brand } from "./Brand";
import { PanelLeftIcon, PlusIcon, SearchIcon, SettingsIcon } from "./icons";

export interface SidebarProps {
  /** Conversation history slot — composed in `app` (cross-feature). */
  history: ReactNode;
  /** Pinned-assistants slot, between nav and history — composed in `app`. */
  pinned?: ReactNode;
  /** Account row slot — a render-prop handed the rail state so the account
   *  component can collapse its identity to just the cog. Defaults to a guest
   *  settings affordance when absent. */
  footer?: (collapsed: boolean) => ReactNode;
  /** Called on any navigation so the mobile drawer can close itself. */
  onNavigate: () => void;
  /** Desktop-only: collapse the sidebar to the slim rail. */
  onCollapse?: () => void;
  /** Desktop-only: expand back from the rail. */
  onExpand?: () => void;
  /**
   * Collapsed (rail) mode — labels, history, and the account row collapse away,
   * but every control keeps a fixed 36px icon column anchored to the same x, so
   * toggling the rail never shifts the icons — only the labels slide out.
   */
  collapsed?: boolean;
}

// Every control shares one shell: a fixed 36px icon column + a label column that
// animates from 1fr (expanded) to 0fr (collapsed). The icon column is anchored,
// so collapsing the rail slides the label away without ever moving the icon. The
// 36px rhythm matches the new-chat button, the toggle, and the brand mark.
const rowShell =
  "grid h-9 cursor-pointer items-center rounded-md outline-none transition-[grid-template-columns,background-color,border-color,color,transform] duration-200 ease-house";
const rowCols = (collapsed: boolean): string =>
  collapsed ? "grid-cols-[36px_0fr]" : "grid-cols-[36px_1fr]";
const iconCell = "grid place-items-center";
// The label: its column shrinks to 0fr while it fades, for a clean exit.
const rowLabel = (collapsed: boolean): string =>
  cn(
    "min-w-0 overflow-hidden whitespace-nowrap pl-1 text-left transition-opacity duration-200 ease-house",
    collapsed ? "opacity-0" : "opacity-100",
  );
// Nav rows: shared colour + hover + active treatment layered onto the shell.
const navRow =
  cn(
    // Resting is FULL ink — never washed out. The active row is distinguished
    // by its tint + edge, not by greying the rest.
    "font-sans text-[13.5px] font-medium text-ink hover:bg-line-soft focus-visible:bg-line-soft",
    // Active: a quiet neutral tint, nothing else — no edge, no border.
    "data-[status=active]:bg-accent-subtle data-[status=active]:font-semibold data-[status=active]:text-accent-text data-[status=active]:[&_svg]:text-accent",
  );

// The settings gear shares one treatment across both rail states: pale at rest
// (text-ink-faint, so it recedes), inking up on hover/focus. `group` lets the
// glyph react to the row's hover.
const settingsGear =
  "group text-ink-soft hover:bg-line-soft hover:text-ink focus-visible:bg-line-soft focus-visible:text-ink";
// The glyph turns a quarter-tooth-row on hover — the gear's 45° symmetry makes a
// 90° rotation settle cleanly, so the motion reads as a mechanical turn.
const settingsGearGlyph =
  "size-[18px] transition-transform duration-500 ease-house group-hover:rotate-90";

export function Sidebar({
  history,
  pinned,
  footer,
  onNavigate,
  onCollapse,
  onExpand,
  collapsed = false,
}: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setOpen } = usePalette();

  function startNewChat(): void {
    // Clear any stuck/errored run first, then navigate — navigation alone won't
    // reset it (and is a no-op when already on /chat).
    requestNewChat();
    onNavigate();
    void navigate({ to: "/chat" });
  }

  return (
    <div
      className={cn(
        "flex h-full min-h-0 w-full flex-col px-4 pb-4 pt-5",
        // While expanded, pin the inner to the full panel width so its content
        // lays out once at 280px and the widening panel merely reveals it (via
        // the aside's overflow-hidden) — no reflow/wrapping twitch on expand.
        // Collapsed stays w-full so the rail's content area is exactly 36px.
        !collapsed && "min-w-[280px]",
      )}
    >
      <div className="flex items-center justify-between gap-2 pb-5">
        {collapsed ? (
          <button
            type="button"
            onClick={onExpand}
            aria-label={t("sidebar.openSidebar")}
            title={t("sidebar.openSidebar")}
            className="group/expand grid size-9 shrink-0 place-items-center rounded-md outline-none transition-colors duration-150 ease-house hover:bg-line-soft focus-visible:ring-[3px] focus-visible:ring-tint"
          >
            <BrandMark
              size={36}
              className="group-hover/expand:hidden group-focus-visible/expand:hidden"
            />
            <PanelLeftIcon className="hidden size-[18px] text-ink-soft group-hover/expand:block group-focus-visible/expand:block" />
          </button>
        ) : (
          <>
            <Brand />
            {onCollapse ? (
              <button
                type="button"
                aria-label={t("sidebar.closeSidebar")}
                title={t("sidebar.closeSidebar")}
                onClick={onCollapse}
                className="hidden size-9 shrink-0 place-items-center rounded-md text-ink-soft outline-none transition-colors duration-150 ease-house hover:bg-line-soft hover:text-ink focus-visible:ring-[3px] focus-visible:ring-tint min-[881px]:grid [&_svg]:size-[18px]"
              >
                <PanelLeftIcon />
              </button>
            ) : null}
          </>
        )}
      </div>

      <Button
        variant="accent"
        className={cn(
          "h-10 w-full gap-0 rounded-pill px-0",
          "grid grid-cols-[1fr_auto_1fr] items-center",
        )}
        onClick={startNewChat}
        aria-label={collapsed ? t("sidebar.newChat") : undefined}
        title={collapsed ? t("sidebar.newChat") : undefined}
      >
        <span />
        <span className="flex items-center">
          <PlusIcon className="size-4 shrink-0" />
          <span
            className={cn(
              "overflow-hidden whitespace-nowrap text-[13.5px] transition-[opacity,max-width] duration-200 ease-house",
              collapsed ? "max-w-0 opacity-0" : "ml-2 max-w-[120px] opacity-100",
            )}
          >
            {t("sidebar.newChat")}
          </span>
        </span>
        <span />
      </Button>

      <button
        type="button"
        onClick={() => {
          onNavigate();
          setOpen(true);
        }}
        aria-label={collapsed ? t("sidebar.search") : undefined}
        title={collapsed ? t("sidebar.search") : undefined}
        className={cn(
          rowShell,
          rowCols(collapsed),
          "mt-3 border border-transparent text-ink hover:border-line hover:bg-paper focus-visible:border-line focus-visible:bg-paper",
        )}
      >
        <span className={iconCell}>
          <SearchIcon className="size-[15px] text-ink-soft" />
        </span>
        <span className={cn(rowLabel(collapsed), "flex items-center gap-2 pr-2")}>
          <span className="flex-1 font-sans text-[13.5px]">{t("sidebar.search")}</span>
          <span className="flex gap-1">
            {formatShortcut("mod+k").map((token, i) => (
              <Kbd key={i}>{token}</Kbd>
            ))}
          </span>
        </span>
      </button>

      <nav className="mt-5 flex flex-col gap-1" aria-label={t("sidebar.appName")}>
        {NAV_ITEMS.map(({ to, labelKey, Icon }) => (
          <Link
            key={to}
            to={to}
            onClick={onNavigate}
            className={cn(rowShell, rowCols(collapsed), navRow)}
            aria-label={collapsed ? t(labelKey) : undefined}
            title={collapsed ? t(labelKey) : undefined}
          >
            <span className={iconCell}>
              <Icon className="size-[18px]" />
            </span>
            <span className={rowLabel(collapsed)}>{t(labelKey)}</span>
          </Link>
        ))}
      </nav>

      {/* Pinned assistants sit between the nav and the history (expanded only).
          Renders nothing when the user has none, so the rhythm is preserved. */}
      {collapsed ? null : pinned}

      {/* Middle: conversation history when expanded; a spacer in the rail. */}
      {collapsed ? (
        <div className="flex-1" />
      ) : (
        <div className="mt-5 flex min-h-0 flex-1 flex-col">{history}</div>
      )}

      {/* Footer: the account component owns its own collapse (identity column
          1fr → 0fr, leaving the cog to slide into the rail). The Sidebar just
          supplies the breathing room and hands it the rail state. Absent a
          footer (a guest), fall back to a bare collapsing settings affordance. */}
      <div className="mt-3 pt-3">
        {footer ? (
          footer(collapsed)
        ) : (
          <Link
            to="/settings"
            search={{ section: "general" }}
            onClick={onNavigate}
            aria-label={t("sidebar.settings")}
            title={t("sidebar.settings")}
            className={cn(
              "grid items-center rounded-md py-1.5 transition-[grid-template-columns,background-color] duration-300 ease-house",
              collapsed ? "grid-cols-[minmax(0,0fr)_36px]" : "grid-cols-[minmax(0,1fr)_36px]",
              settingsGear,
            )}
          >
            <span
              className={cn(
                "min-w-0 overflow-hidden pl-2 transition-opacity duration-200 ease-house",
                collapsed ? "pointer-events-none opacity-0" : "opacity-100",
              )}
            >
              <span className="truncate font-sans text-[13.5px] font-medium">
                {t("sidebar.settings")}
              </span>
            </span>
            <span className="grid size-9 place-items-center">
              <SettingsIcon className={settingsGearGlyph} />
            </span>
          </Link>
        )}
      </div>
    </div>
  );
}
